import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router'
import { Dispatch } from '@reduxjs/toolkit'

import { usePrevious } from '../hooks'
import { killState } from '../rootReducer'
import { TrackingService } from '../tracking/tracking-service'
import type { TrackingStatusType } from '../tracking/types'
import { GlobalState } from '../types'
import type { UploadIdentityType } from '../upload/types'
import { uploadClearMost } from '../upload/upload-actions'
import { getTrackingError } from '../upload/upload-selectors'
import { getFilesByStatus, getIdentityValues } from './confirmation-selectors'
import { FailedPage } from './FailedPage'
import { PartialFailurePage } from './PartialFailurePage'
import { Container } from './styled-components'
import { SuccessPage } from './SuccessPage'
import type { FilesByStatus, FileStatus } from './types'
import {
  FAILURE_UI_CONFIG,
  PARTIAL_FAILURE_UI_CONFIG,
  SUCCESS_UI_CONFIG,
  TRACKING_FAILURE_UI_CONFIG
} from './UIConfig'

type ConfirmationProps = {
  killState(): void
  clearMost(): void
  trackingError: boolean
  identity: UploadIdentityType
  claimNumber: string
  files: FilesByStatus
}

type ConfirmationState = { redirectToTravelers: boolean }

const ConfirmationComponent = (props: ConfirmationProps) => {
  const { trackingError, identity, claimNumber, files, killState, clearMost } = props
  const [state, setState] =
    useState<ConfirmationState>({ redirectToTravelers: false })
  const prevState = usePrevious<ConfirmationState>(state)
  const navigate = useNavigate()
  const trackingService = TrackingService.getInstance()
  const { successes, unsupportedErrors, networkErrors, lostConnectionErrors } = files
  const failures = useMemo(() => {
    return [...unsupportedErrors, ...networkErrors, ...lostConnectionErrors]
  }, [unsupportedErrors, networkErrors, lostConnectionErrors])

  const successUpload = files.successes.length === 0 ? false : true
  const failureUpload = files.unsupportedErrors.length === 0
    && files.networkErrors.length === 0
    && files.lostConnectionErrors.length === 0 ? false : true

  const textColor = '#46494D'
  const successSvgColor = '#27A555'
  const failureSvgColor = '#EF7A00'
  const successHref = `${window.travURI}icons/symbol-sprites.svg#check-circle-1-18`
  const failureHref = `${window.travURI}icons/symbol-sprites.svg#ban-1-18`

  const getResultType = useCallback(
    (
      successResult: FileStatus[],
      failureResult: FileStatus[],
      trackingErrorResult?: boolean
    ) => {
      if (successResult.length && failureResult.length === 0 && !trackingErrorResult)
        return SUCCESS_UI_CONFIG
      if (successResult.length && failureResult.length === 0 && trackingErrorResult)
        return TRACKING_FAILURE_UI_CONFIG
      if (failureResult.length && successResult.length === 0 && !trackingErrorResult)
        return FAILURE_UI_CONFIG
      if (failureResult.length && successResult.length === 0 && trackingErrorResult)
        return FAILURE_UI_CONFIG
      return PARTIAL_FAILURE_UI_CONFIG
    },
    []
  )

  const uiConfig = useMemo(() => {
    return getResultType(successes, failures, trackingError)
  }, [getResultType, failures, successes, trackingError])

  const updateInitialTrackingStatus = useCallback(
    async (file: FileStatus, isSuccessful: boolean) => {
      if (!trackingService) return
      if (file && file.batchId && file.trackingId) {
        const trackingStatus: TrackingStatusType = {
          batchId: file.batchId,
          trackingId: file.trackingId,
          fileName: file.fileName,
          status: isSuccessful ? 'In Progress' : 'Error',
        }

        try {
          await trackingService.updateTrackingStatus(trackingStatus)
        } catch (error) {
          if (error instanceof Error) {
            console.log(`tracking error status and message: ${error.message}`)
          }
        }
      }
    },
    [trackingService]
  )

  // updated from componentDidMount
  useEffect(() => {
    document.title = 'File Uploads - Confirmation'

    for (const file of failures) {
      updateInitialTrackingStatus(file, false)
    }

    for (const file of successes) {
      updateInitialTrackingStatus(file, true)
    }

  }, [
    getResultType,
    updateInitialTrackingStatus,
    successes,
    failures,
    unsupportedErrors
  ])

  const redirectToTravelers = useCallback(() => {
    navigate(`${window.travURI}`)
  }, [navigate])

  // updated from componentDidUpdate
  useEffect(() => {
    if (prevState && !prevState.redirectToTravelers && state.redirectToTravelers) {
      redirectToTravelers()
    }
  }, [prevState, state, redirectToTravelers])

  const onExit = useCallback(() => {
    setState({ redirectToTravelers: true })
  }, [setState])

  const onFindClaimClick = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault()
    killState()
    if (window.onbeforeunload) window.onbeforeunload = null
    onExit()
  }, [killState, onExit])

  const onBackToUploadPage = useCallback(() =>
    (event: React.SyntheticEvent) => {
      event.preventDefault()

      clearMost()
      navigate(`${window.travURI}upload`)
    },
    [clearMost, navigate]
  )

  return (
    <Container>
      {successUpload && !failureUpload &&
        <SuccessPage
          uiConfig={uiConfig}
          claimNumber={claimNumber}
          successes={successes}
          textColor={textColor}
          successSvgColor={successSvgColor}
          successHref={successHref}
          trackingError={trackingError}
          identity={identity}
          onBackToUploadPage={onBackToUploadPage}
          onFindClaimClick={onFindClaimClick}
        />
      }
      {successUpload && failureUpload &&
        <PartialFailurePage
          uiConfig={uiConfig}
          claimNumber={claimNumber}
          successes={successes}
          textColor={textColor}
          unsupportedErrors={unsupportedErrors}
          networkErrors={networkErrors}
          lostConnectionErrors={lostConnectionErrors}
          failureHref={failureHref}
          failureSvgColor={failureSvgColor}
          successSvgColor={successSvgColor}
          successHref={successHref}
          trackingError={trackingError}
          identity={identity}
          onBackToUploadPage={onBackToUploadPage}
          onFindClaimClick={onFindClaimClick}
        />
      }
      {!successUpload && failureUpload &&
        <FailedPage
          uiConfig={uiConfig}
          claimNumber={claimNumber}
          textColor={textColor}
          unsupportedErrors={unsupportedErrors}
          networkErrors={networkErrors}
          lostConnectionErrors={lostConnectionErrors}
          failureHref={failureHref}
          failureSvgColor={failureSvgColor}
          onBackToUploadPage={onBackToUploadPage}
          onFindClaimClick={onFindClaimClick}
        />
      }
    </Container>
  )
}

const mapStateToProps = (state: GlobalState) => ({
  files: getFilesByStatus(state),
  claimNumber: state.claim.claimDetails.claim,
  identity: getIdentityValues(state),
  trackingError: getTrackingError(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  clearMost: () => dispatch(uploadClearMost()),
  killState: () => dispatch(killState())
})

const Confirmation =
  connect(mapStateToProps, mapDispatchToProps)(ConfirmationComponent)

export {
  Confirmation,
  ConfirmationComponent
}
